import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Chip,
  CircularProgress
} from '@mui/material';
import { ViewList, ViewModule, School, AutoAwesomeMotion, CalendarMonth } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import { courseService } from '../../services/api';
import '../Course/CourseGrid.css'; // Import the same CSS used by CatlogCard
import { useEmbedded } from '../../hooks/useEmbedded';
import { tokens } from '../../theme';

const AllInstances = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams(); // Get course ID from URL params
  const location = useLocation();
  const stateData = location.state;
  const isEmbedded = useEmbedded();

  useEffect(() => {
    // If we have state data, use it directly
    if (stateData && stateData.instances) {
      setCourseData(stateData);
      setLoading(false);
      return;
    }

    // Otherwise, fetch the data
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        const response = await courseService.getCoursePreview(id);
        console.log('API Response for instances:', response.data);
        setCourseData(response.data);
      } catch (err) {
        console.error('Error fetching course data:', err);
        setError('Failed to load course instances');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [id, stateData]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleSelectInstance = (instanceId) => {
    const pathPrefix = isEmbedded ? '/embed' : '';
    navigate(`${pathPrefix}/learner/course/${id}/${instanceId}`);
  };

  // Update the "Back to Course" button navigation
  const handleBackToCourse = () => {
    const pathPrefix = isEmbedded ? '/embed' : '';
    navigate(`${pathPrefix}/learner/course/${courseId}`);
  };

  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('MMM DD, YYYY') : 'Not specified';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !courseData) {
    return (
      <Container>
        <Typography variant="h6" color="error" align="center">
          {error || 'Course instances not found'}
        </Typography>
      </Container>
    );
  }

  // Extract the necessary data from courseData
  const instances = courseData?.instances || [];
  const enrolledInstanceId = courseData?.enrolledInstanceId || courseData?.enrolledObj?.instance_id;
  const progress = courseData?.progress;
  const courseId = courseData?.courseId || id;
  const courseName = courseData?.course?.name || 'Course';

  // Handle the case when instances is undefined or empty
  if (!instances || instances.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '50vh' }}>
          <Typography variant="h5" gutterBottom>No instances available</Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate(`/courses/${courseId}`)}
            sx={{ mt: 2 }}
          >
            Back to Course
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{courseName} - Instances</Typography>
        <Box>
          <Button 
            variant="outlined" 
            onClick={handleBackToCourse}
            sx={{ mr: 2 }}
          >
            Back to Course
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {instances.map((instance) => (
            <Grid item xs={12} sm={6} md={4} key={instance.id}>
              <div className="course-card-wrapper">
                <div className="course-card">
                  <div className="hover-overlay">          
                    <Box className="course-description">
                      <Typography 
                        component="div"
                        className="card-description"
                      >
                        {instance.description || `${instance.completion_deadline ? `Complete by ${formatDate(instance.completion_deadline)}.` : ''}`}
                      </Typography>
                    </Box>
                  </div>
                  
                  {/* Icon instead of image */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%', 
                    backgroundColor: '#f5f5f5'
                  }}>
                    <AutoAwesomeMotion style={{ 
                      fontSize: 80, 
                      color: enrolledInstanceId === instance.id ? '#4caf50' : '#2563eb' 
                    }} />
                  </div>
                  
                  {/* Progress Bar at Bottom (if enrolled) */}
                  {enrolledInstanceId === instance.id && progress && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: '#e0e0e0',
                      borderBottomLeftRadius: '8px',
                      borderBottomRightRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${progress.progress}%`,
                        height: '100%',
                        backgroundColor: '#2563eb'
                      }}></div>
                    </div>
                  )}
                </div>
                
                <div className="card-footer">
                  <Box sx={{ mb: 2 }}>
                    <h3 className="card-title">
                      {instance.title || `Instance ${instance.id}`}
                    </h3>
                    <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {instance.default_instance === '1' && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      )}
                    </Typography>
                    
                    {instance.completion_deadline && (
                      <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
                        Complete by: {formatDate(instance.completion_deadline)}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 2, mb: 5, textAlign: 'right' }}>
                    {enrolledInstanceId === instance.id ? (
                      <Button
                        variant="contained"
                        style={{ backgroundColor: '#2563eb' }}
                        onClick={() => handleSelectInstance(instance.id)}
                        sx={{ px: 3, py: 1 }}
                      >
                        Current
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        style={{ backgroundColor: '#2563eb' }}
                        onClick={() => handleSelectInstance(instance.id)}
                        sx={{ px: 3, py: 1 }}
                      >
                        Select
                      </Button>
                    )}
                  </Box>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {instances.map((instance) => (
            <Card 
              key={instance.id} 
              sx={{ 
                mb: 2, 
                border: enrolledInstanceId === instance.id ? '2px solid #4caf50' : 'none'
              }}
            >
              <CardContent>
                <Grid container alignItems="center">
                  <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <School style={{ 
                      fontSize: 40, 
                      color: enrolledInstanceId === instance.id ? '#4caf50' : '#2563eb' 
                    }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="h6">
                      {instance.title || `Instance ${instance.id}`}
                      {instance.default_instance === '1' && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {instance.completion_deadline && (
                      <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
                        Complete by: {formatDate(instance.completion_deadline)}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {enrolledInstanceId === instance.id && progress && (
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                          Progress: {Math.round(progress.progress)}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={progress.progress} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 3,
                            backgroundColor: tokens.color.bgTertiary,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: progress.progress >= 100 ? tokens.color.positive : tokens.color.brand,
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                    <Button 
                      variant="contained" 
                      style={{ backgroundColor: '#2563eb' }}
                      onClick={() => handleSelectInstance(instance.id)}
                      size="small"
                    >
                      {enrolledInstanceId === instance.id ? "Current" : "Select"}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default AllInstances;